import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Comment, MessageEntityType } from './comments.model';
import { AuthService } from '@aiwyn/auth';
import { getFirstAndLastNameFromFullName } from './comments.utils';
import { isEmpty } from '@aiwyn/shared';

interface MessageViewResponse {
  viewedAt: string;
}

interface MessageResponse {
  id: number;
  entityType: MessageEntityType;
  entityId: number;
  parentMessageId: number | null;
  content: string;
  createdByStaffId: number | null;
  createdByContactId: number | null;
  createdAt: string;
  updatedAt: string;
  createdByStaff?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    fullName: string;
    email: string;
  } | null;
  createdByContact?: {
    id: number;
    username: string;
    contact_info: {
      email: string | null;
      full_name: string | null;
      firstName: string | null;
      lastName: string | null;
    };
  } | null;
  viewedAt: MessageViewResponse[];
  replies?: MessageResponse[];
}

interface CreateMessageRequest {
  entityType: MessageEntityType;
  entityId: number;
  content: string;
  parentMessageId?: number | null;
}

/**
 * API Service for managing messages (comments) via GraphQL
 * Based on ART-6424 Threaded Messaging System Implementation
 */
@Injectable({ providedIn: 'root' })
export class CommentsApiService {
  private readonly apollo = inject(Apollo);
  private readonly authService = inject(AuthService);

  /**
   * Query messages for one or more entities
   * Fetches parent messages with their replies ordered by creation date
   */
  getMessages(entityIds: number[], entityType: MessageEntityType, limit = 50, shouldMaskContent = false): Observable<Comment[]> {
    if (isEmpty(entityIds)) {
      return of([]);
    }

    return this.authService.getUser().pipe(
      take(1),
      map((user) => {
        const contactId = user?.getContactId();
        if (!contactId) {
          throw new Error('Missing Contact Id');
        }
        return contactId;
      }),
      switchMap((contactId) =>
        this.apollo.query<{ message: MessageResponse[] }>({
          query: gql`
            query GetMessages($entityIds: [bigint!]!, $entityType: String!, $contactId: Int!, $limit: Int!) {
              message(
                where: { entityId: { _in: $entityIds }, entityType: { _eq: $entityType }, parentMessageId: { _is_null: true } }
                order_by: { createdAt: desc }
                limit: $limit
              ) {
                id
                entityType
                entityId
                parentMessageId
                ${shouldMaskContent ? '' : 'content'}
                createdByStaffId
                createdByContactId
                createdAt
                updatedAt
                createdByStaff {
                  id
                  firstName
                  lastName
                  fullName
                  email
                }
                createdByContact {
                  id
                  username
                  contact_info {
                    email
                    full_name
                    firstName
                    lastName
                  }
                }
                viewedAt: message_views(where: { contactId: { _eq: $contactId } }) {
                  viewedAt
                }
                replies(order_by: { createdAt: asc }) {
                  id
                  ${shouldMaskContent ? '' : 'content'}
                  createdAt
                  createdByStaffId
                  createdByContactId
                  createdByStaff {
                    id
                    firstName
                    lastName
                    fullName
                    email
                  }
                  createdByContact {
                    id
                    username
                    contact_info {
                      email
                      full_name
                      firstName
                      lastName
                    }
                  }
                  viewedAt: message_views(where: { contactId: { _eq: $contactId } }) {
                    viewedAt
                  }
                }
              }
            }
          `,
          variables: {
            entityIds,
            entityType,
            contactId,
            limit,
          },
          fetchPolicy: 'network-only', // Always fetch fresh data
        }),
      ),
      map(({ data }) => data.message.map((msg) => this.mapMessageToComment(msg))),
    );
  }

  /**
   * Mark a message as viewed
   * Returns the viewedAt timestamp (or null if already viewed)
   */
  markMessageAsViewed(messageId: number, contactId: number): Observable<{ viewedAt: string | null }> {
    return this.apollo
      .mutate<{ insert_message_viewed_one: { viewedAt: string | null } }>({
        mutation: gql`
          mutation MarkedAsViewed($messageId: Int!, $contactId: Int!) {
            insert_message_viewed_one(
              object: { messageId: $messageId, contactId: $contactId }
              on_conflict: { constraint: uq_message_viewed_message_contact, update_columns: [] }
            ) {
              viewedAt
            }
          }
        `,
        variables: {
          messageId,
          contactId,
        },
      })
      .pipe(map(({ data }) => data.insert_message_viewed_one));
  }

  /**
   * Mark several messages as viewed by several contacts in ONE call — the plural
   * `insert_message_viewed`, one object per (message, contact) pair.
   *
   * A user can hold more than one contact record (login picks the first, and the
   * read filters run over the whole linked set), so a view is recorded for every
   * one of their contact ids; anything else leaves surfaces reading through
   * different ids disagreeing about what has been read. `on_conflict` on
   * `uq_message_viewed_message_contact` makes repeats harmless.
   */
  markMessagesAsViewed(messageIds: number[], contactIds: number[]): Observable<number> {
    const objects = messageIds.flatMap((messageId) => contactIds.map((contactId) => ({ messageId, contactId })));
    if (isEmpty(objects)) {
      return of(0);
    }

    return this.apollo
      .mutate<{ insert_message_viewed: { affected_rows: number } | null }>({
        mutation: gql`
          mutation MarkManyAsViewed($objects: [message_viewed_insert_input!]!) {
            insert_message_viewed(
              objects: $objects
              on_conflict: { constraint: uq_message_viewed_message_contact, update_columns: [] }
            ) {
              affected_rows
            }
          }
        `,
        variables: { objects },
      })
      .pipe(map(({ data }) => data?.insert_message_viewed?.affected_rows ?? 0));
  }

  /**
   * Create a new message (comment or reply)
   * Uses GraphQL mutation as specified in the contract
   */
  createMessage(request: CreateMessageRequest): Observable<Comment> {
    return this.apollo
      .mutate<{ CreateMessage: MessageResponse }>({
        mutation: gql`
          mutation CreateMessage($request: CreateMessageRequest!) {
            CreateMessage(request: $request) {
              id
              entityType
              entityId
              parentMessageId
              content
              createdByStaffId
              createdByContactId
              createdAt
              updatedAt
            }
          }
        `,
        variables: {
          request,
        },
      })
      .pipe(map(({ data }) => this.mapMessageToComment(data.CreateMessage)));
  }

  /**
   * Maps API MessageResponse to UI Comment model
   */
  private mapMessageToComment(message: MessageResponse): Comment {
    // Determine author from either staff or contact
    const authUserData = this.authService.getUserSnapshot() ?? null;
    const isStaff = message.createdByStaffId !== null;

    // Author ID for avatar component
    const authorId = message.createdByStaffId || message.createdByContactId || 0;

    let firstName = 'Unknown';
    let lastName = 'User';

    if (isStaff && message.createdByStaff) {
      const fullName = getFirstAndLastNameFromFullName(message.createdByStaff.fullName ?? 'Unknown User');
      firstName = message.createdByStaff.firstName ?? fullName.firstName;
      lastName = message.createdByStaff.lastName ?? fullName.lastName;
    } else if (authUserData?.getContactId() === message.createdByContactId) {
      const author = getFirstAndLastNameFromFullName(
        message.createdByContact?.contact_info?.full_name ?? authUserData?.getDisplayName() ?? 'Unknown User',
      );
      firstName = message?.createdByContact?.contact_info?.firstName ?? author.firstName;
      lastName = message?.createdByContact?.contact_info?.lastName ?? author.lastName;
    } else if (!isStaff && message.createdByContact) {
      const fullName = getFirstAndLastNameFromFullName(
        message.createdByContact.contact_info?.full_name ?? message.createdByContact.username,
      );
      firstName = message.createdByContact.contact_info?.firstName || fullName.firstName;
      lastName = message.createdByContact.contact_info?.lastName || fullName.lastName;
    }

    // Message is unread if viewedAt array is empty (no views recorded for this staff member)
    const wasCreatedByUser = authUserData?.getContactId() === message.createdByContactId;
    const isMessageUnread = wasCreatedByUser ? false : !message.viewedAt || message.viewedAt.length === 0;

    const replies = message.replies?.map((reply) => this.mapMessageToComment(reply)) || [];
    const isUnread = replies.some((reply) => reply.isUnread) ? true : isMessageUnread;

    return {
      id: message.id.toString(),
      entityId: message.entityId,
      entityType: message.entityType,
      parentMessageId: message.parentMessageId?.toString() || null,
      author: {
        id: authorId.toString(),
        firstName,
        lastName,
        isStaff,
      },
      text: message.content,
      timestamp: moment.utc(message.createdAt).toDate(),
      replies,
      isUnread,
      isMasked: message.content == undefined,
      createdAt: message.createdAt,
    };
  }
}
